// beepSound.ts
export interface SoundOptions {
    frequency?: number;      // Высота тона (800-1500 Гц)
    duration?: number;       // Длительность (50-500 мс)
    volume?: number;         // Громкость (0-1)
    type?: OscillatorType;   // Тип волны
}

// Глобальный контекст
let audioContext: AudioContext | null = null;

const getAudioContext = (): AudioContext | null => {
    if (!audioContext) {
        const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
        if (!AudioContextClass) {
            console.warn('Web Audio API не поддерживается');
            return null;
        }
        
        try {
            audioContext = new AudioContext();
            
            // Автоматически восстанавливаем при user interaction
            const resumeContext = () => {
                if (audioContext && audioContext.state === 'suspended') {
                    audioContext.resume();
                }
                document.removeEventListener('click', resumeContext);
                document.removeEventListener('keydown', resumeContext);
                document.removeEventListener('touchstart', resumeContext);
            };
            
            document.addEventListener('click', resumeContext);
            document.addEventListener('keydown', resumeContext);
            document.addEventListener('touchstart', resumeContext);
            
        } catch (error) {
            console.warn('Не удалось создать AudioContext:', error);
            return null;
        }
    }
    
    // Всегда пытаемся возобновить
    if (audioContext.state === 'suspended') {
        audioContext.resume().catch(() => {});
    }
    
    return audioContext;
};

export const playBeepSound = (options: SoundOptions = {}): void => {
    try {
        const context = getAudioContext();
        if (!context) return;
        
        // Значения по умолчанию
        const defaultOptions: Required<SoundOptions> = {
            frequency: 1200,
            duration: 300,
            volume: 0.8,
            type: 'square'
        };
        
        // Объединяем с переданными параметрами
        const finalOptions = { ...defaultOptions, ...options };
        
        // Ограничиваем volume 0-1 (без этого звук будет тихим или пропадет)
        const safeVolume = Math.max(0, Math.min(1, finalOptions.volume));
        
        const oscillator = context.createOscillator();
        const gainNode = context.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(context.destination);
        
        oscillator.frequency.value = finalOptions.frequency;
        oscillator.type = finalOptions.type;
        
        const now = context.currentTime;
        const durationInSeconds = finalOptions.duration / 1000;
        
        // УБИРАЕМ exponentialRampToValueAtTime - он делает звук тихим
        // Используем более простой подход
        gainNode.gain.setValueAtTime(0, now);
        gainNode.gain.linearRampToValueAtTime(safeVolume, now + 0.01);
        
        // Держим громкость
        gainNode.gain.setValueAtTime(safeVolume, now + 0.01);
        gainNode.gain.linearRampToValueAtTime(0, now + durationInSeconds);
        
        oscillator.start(now);
        oscillator.stop(now + durationInSeconds);
        
        // НЕ закрываем контекст!
        // Просто очищаем ноды
        oscillator.onended = () => {
            try {
                oscillator.disconnect();
                gainNode.disconnect();
            } catch (e) {
                // Игнорируем
            }
        };
        
    } catch (error) {
        console.log('Не удалось воспроизвести звук:', error);
    }
};

// Функция для алертов с пресетами (ВАШИ настройки)
export const playAlertSound = (
    variant: 'danger' | 'warning' | 'success' | 'info' = 'danger',
    customOptions?: SoundOptions
): void => {
    const soundPresets: Record<string, SoundOptions> = {
        'danger': { 
            frequency: 150, 
            duration: 300, 
            volume: 0.3,    // Будет ограничено до 1
            type: 'square' 
        },
        'warning': { 
            frequency: 800, 
            duration: 200, 
            volume: 0.5, 
            type: 'sawtooth' 
        },
        'success': { 
            frequency: 1200, 
            duration: 850, 
            volume: 2.5,    // Будет ограничено до 1
            type: 'sine' 
        },
        'info': { 
            frequency: 1000, 
            duration: 100, 
            volume: 0.4, 
            type: 'triangle' 
        }
    };
    
    const preset = soundPresets[variant] || soundPresets.danger;
    playBeepSound({ ...preset, ...customOptions });
};